// @ts-nocheck
import React, { useState, useEffect } from 'react';
import Search from '@/components/search';
import Breadcrumbs from '@/components/breadcrumbs';
import { colors } from '@/theme';

import CellStatus from '@/components/cells/status';
import CellDate from '@/components/cells/date';
import CellBold from '@/components/cells/bold';
import CellBool from '@/components/cells/bool';
import CellEmployee from '@/components/cells/employee';
import CellDescription from '@/components/cells/description';

import KendoTable from '@/components/kendo/kendo-table';
import KendoCellStatus from '@/components/kendo/cells/kendo-cell-status';
import KendoCellEmployee from '@/components/kendo/cells/kendo-cell-employee';
import KendoCellDate from '@/components/kendo/cells/kendo-cell-date';
import KendoCellBool from '@/components/kendo/cells/kendo-cell-bool';

import KendoPager from '@/components/kendo/kendo-pager';
import Router from 'next/router';
import Fetch from '@/utils/Fetch';
import SettingsMenu from '@/components/settings/menu';
import Storage from '@/utils/storage';
import * as Enums from '@/utils/enums';
import Helper from '@/utils/helper';
import CellWide from '@/components/cells/wide';
import { createFileRoute } from '@tanstack/react-router'



const warehouseColumns: Array<{ Label: string; ColumnName: string; CellType: string; UserWidth?: number }> = [{
    Label: 'Code',
    ColumnName: 'Code',
    CellType: 'none',
}, {
    Label: 'Name',
    ColumnName: 'Name',
    CellType: 'none',
}, {
    Label: 'Type',
    ColumnName: 'WarehouseType',
    CellType: 'status',
}, {
    Label: 'Employee',
    ColumnName: 'EmployeeFullName',
    CellType: 'employee',
}, {
    Label: 'Description',
    ColumnName: 'Description',
    CellType: 'none',
}, {
    Label: 'Default',
    ColumnName: 'IsDefault',
    CellType: 'icon',
}, {
    Label: 'Created',
    ColumnName: 'CreatedDate',
    CellType: 'date',
}, {
    Label: 'Created By',
    ColumnName: 'CreatedBy',
    CellType: 'none',
}, {
    Label: 'Modified',
    ColumnName: 'ModifiedDate',
    CellType: 'date',
}, {
    Label: 'Modified By',
    ColumnName: 'ModifiedBy',
    CellType: 'none',
}];

const warehouseAncillaryFilters = {
    IncludeDisabled: [{
        type: Enums.ControlType.Switch,
        label: 'Include disabled warehouses',
    }],
};

function WarehouseList() {

    let currPath = '';
    if (Router.router) {
        currPath = Router.router.asPath;
    }

    const [columnState, setColumnState] = useState(warehouseColumns);

    const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);

    const getAccessStatus = () => {
        let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
        if (subscriptionInfo) {
            setAccessStatus(subscriptionInfo.AccessStatus);
        }
    };

    const columns = React.useMemo(
        () => columnState.map(function (column: any) {
            let columnObject = {
                Header: column.Label,
                accessor: column.ColumnName,
                // for table resize columns
                ColumnName: column.ColumnName,
                UserWidth: column.UserWidth
                // for table resize columns
            };

            if (column.CellType != "none") {
                switch (column.CellType) {
                    case 'employee':
                        columnObject['Cell'] = ({ cell: { value } }: any) => <CellEmployee value={value} />;
                        columnObject['KendoCell'] = (props: any) => <KendoCellEmployee {...props} employeesField="Employees" />;
                        break;
                    case 'date':
                        columnObject['Cell'] = ({ cell: { value } }: any) => <CellDate value={value} />;
                        columnObject['KendoCell'] = (props: any) => <KendoCellDate {...props} />;
                        break;
                    case 'status':
                        columnObject['Cell'] = ({ cell: { value } }: any) => <CellStatus value={value} />;
                        columnObject['KendoCell'] = (props: any) => <KendoCellStatus {...props} />;
                        break;
                    case 'bold':
                        columnObject['Cell'] = ({ cell: { value } }: any) => <CellBold value={value} />;
                        break;
                    case 'icon':
                        columnObject['Cell'] = ({ cell: { value } }: any) => <CellBool value={value} />;
                        columnObject['KendoCell'] = (props: any) => <KendoCellBool {...props} />;
                        break;
                    case 'Description':
                        columnObject['Cell'] = ({ cell: { value } }: any) => <CellDescription value={value} />;
                        break;
                    default:
                        columnObject['Cell'] = ({ cell: { value } }: any) => <CellBold value={value} />;
                }
            }

            if (column.ColumnName == 'WarehouseType') {
                columnObject['KendoCell'] = (props: any) => <KendoCellStatus {...props} valueEnum="WarehouseType"/>;
            }

            return columnObject;
        }),
        [columnState]
    );

    const [warehouseResults, setWarehouseResults] = useState<any[]>([]);
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');
    const [searching, setSearching] = useState(false);
    const [searchVal, setSearchVal] = useState('');
    const [ancillaryFilters, setAncillaryFilters] = useState({ IncludeDisabled: false });
    const [totalResults, setTotalResults] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const pageSizeChanged = (size: any) => {
        setPageSize(size);
    };

    const pageChanged = (page: any) => {
        setCurrentPage(page);
    };

    const handleAncillaryFilterChange = (result: any) => {
        if (result.reset) {
            setAncillaryFilters({ IncludeDisabled: false });
        } else {
            setAncillaryFilters({ IncludeDisabled: result.checked });
        }
    };

    useEffect(() => {
        if (currentPage == 1) {
            searchWarehouses();
        } else {
            setCurrentPage(1);
        }
    }, [sortField, sortDirection, pageSize, ancillaryFilters]);

    useEffect(() => {
        searchWarehouses();
    }, [currentPage]);

    useEffect(() => {
        getAccessStatus();
    }, []);

    useEffect(() => {
        if (accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
            Helper.nextRouter(Router.replace, "/");
        }
    }, [accessStatus]);

    function setSort(field: any) {
        setSortDirection(Helper.getSortDirection(field, sortField, sortDirection));
        setSortField(field);
    }

    async function searchWarehouses() {
        setSearching(true);

        const warehouses = await Fetch.post({
            url: `/Warehouse/GetWarehouses`,
            params: {
                pageSize: pageSize,
                pageIndex: (currentPage - 1),
                searchPhrase: searchVal,
                sortExpression: sortField,
                sortDirection: sortDirection,
                IncludeDisabled: ancillaryFilters["IncludeDisabled"],
            }
        });
        setWarehouseResults(warehouses.Results);
        setTotalResults(warehouses.TotalResults);

        setSearching(false);
    }

    function rowClick(row: any) {
        Helper.nextRouter(Router.push, `/settings/warehouse/[id]`, `/settings/warehouse/${row.original.ID}`);
    }

    const createWarehouse = () => {
        Helper.nextRouter(Router.push, "/settings/warehouse/create", "/settings/warehouse/create");
    }

    return (
        <div className="list-container">
            <div className="row">
                <div className="title">
                    <Breadcrumbs
                        crumbs={[
                            { text: 'Settings', link: '', suppressLink: true },
                            { text: 'Warehouses', link: '/settings/warehouse/list' },
                        ]}
                    />
                </div>
            </div>
            <div className="row">
                <SettingsMenu urlPath={currPath} />
                <div className="settings-card">

                    <div className="row end">
                        <div className="search-container">
                            <Search
                                placeholder="Search Name, Code or Email"
                                resultsNum={warehouseResults.length}
                                searchVal={searchVal}
                                setSearchVal={setSearchVal}
                                searchFunc={searchWarehouses}
                                ancillaryFilters={warehouseAncillaryFilters}
                                setAncillaryFilters={handleAncillaryFilterChange}
                            />
                        </div>

                        {/* <Button disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                            text="Create" icon="plus-circle" extraClasses="fit-content no-margin" onClick={createWarehouse} /> */}
                    </div>

                    <div className={"no-items" + (warehouseResults.length == 0 ? " no-items-visible" : "")}>
                        <div className={"loading-overlay" + (searching ? " loading-overlay-visible" : "")}>
                            <div className="loader"></div>
                        </div>
                        <img src="/job-folder.svg" alt="Warehouse Folder" />
                        <h3>No warehouses found</h3>
                        <p>If you can't find a warehouse, try another search or create a new one.</p>
                        <a href="/settings/warehouse/create"><img src="/icons/plus-circle-blue.svg" alt="plus" /> Add new warehouse</a>
                        <img className="wave" src="/wave.svg" alt="wave" />
                    </div>

                    <div className="margin-top">
                        {warehouseResults.length != 0 ? <KendoTable
                            searching={searching}
                            actions={[
                                { text: "Edit", icon: "edit", function: (row: any) => Helper.nextRouter(Router.push, `/settings/warehouse/${row.ID}`) },
                            ] as any}
                            columns={columns}
                            data={warehouseResults}
                            rowClick={rowClick}
                            setSort={setSort}
                            sortField={sortField}
                            sortDirection={sortDirection}
                            type="Warehouse"
                            heightOffset={300}
                            highlightColumnName="Name"

                            // defaults
                            onColumnResize={() => { }}
                            canSelectItems={false}
                            selectedItems={[]}
                            setSelectedItems={() => { }}
                            highlightColumnLink={null}
                        /> : ""}
                    </div>

                    <KendoPager pageSizeChanged={pageSizeChanged} pageChanged={pageChanged} totalResults={totalResults} searchValue={searchVal} parentPageNumber={currentPage} />

                </div>
            </div>

            <style jsx>{`
        .inverted {
          background-color: ${colors.blueDark};
        }
        .column {
          width: 100%;
        }
        .column-margin {
          margin-left: 24px;
        }
        .button-container {
          flex-shrink: 0;
          width: 10rem;
        }
        .button-container :global(.button){
          margin-top: 0.5rem;
        }
        .row {
          display: flex;
          justify-content: space-between;
        }
        .end {
          align-items: flex-end;
        }
        .row.top-gap {
          margin-top: 2.5rem;
        }
        .search-container :global(.search) {
          width: 528px;
        }
        a {
          text-decoration: none;
        }
        .margin-top {
          margin-top: 0.5rem;
        }
      `}</style>
        </div>
    );
}

export default WarehouseList;

export const Route = createFileRoute('/_authenticated/settings/warehouse/list')({ component: WarehouseList })
